// src/hooks/useScrollReveal.js
import { useEffect, useRef, useState } from "react";

/**
 * useScrollReveal
 * Returns a ref to attach to any element + a boolean `isVisible`.
 *
 * @param {object} options
 * @param {number}  options.threshold  - 0–1, how much of the element must be visible (default 0.15)
 * @param {boolean} options.once       - only trigger once, don't re-hide on scroll up (default true)
 */
const useScrollReveal = ({ threshold = 0.15, once = true } = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(el); // stop watching after first reveal
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.unobserve(el);
  }, [threshold, once]);

  return { ref, isVisible };
};

export default useScrollReveal;